import React, { memo, useMemo } from 'react';
import { ButtonBase, createStyles, Icon, makeStyles, Chip } from '@material-ui/core';
import { RemoveCircleOutline } from '@material-ui/icons';
import Anesthesiologist from '@/types/Anesthesiologist';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { list as listAnesthesiologists } from '@/graph/anesthesiologists';
import { editProcedureAnesthesiologist } from '@/graph/procedures';
import PopupSelectMenu from '../shared/PopupSelectMenu';
import AnesthesiaIcon from '@/assets/images/anesthesia.svg?react';
import { fullNameToLastNameRest } from '../shared/procedureUtils';

const AnesthesiologistName = ({
  date,
  procedureId,
  editableAnesthesiologist,
  anesthesiologistId,
  anesthesiologistName,
  condensView,
  onError,
}: {
  date?: Date;
  procedureId: number;
  editableAnesthesiologist?: boolean;
  anesthesiologistId?: number;
  anesthesiologistName?: string;
  condensView?: boolean;
  onError?: (error: ApolloError, operation: 'load' | 'save') => void;
}) => {
  const classes = useStyles();

  const { data, error } = useQuery(listAnesthesiologists, {
    onError: onError ? error => onError(error, 'load') : undefined,
  });
  const anesthesiologists: Anesthesiologist[] = useMemo(() => data?.anesthesiologists || [], [data?.anesthesiologists]);

  const options = useMemo(
    () =>
      data
        ? anesthesiologists.map(a => ({
            id: a.id,
            label: a.name,
          }))
        : error
          ? []
          : undefined,
    [data, error, anesthesiologists]
  );

  const selectedId = useMemo(() => {
    if (anesthesiologistId != null) return anesthesiologistId;

    const label = (anesthesiologistName || '').toLowerCase();
    if (!label) return undefined;

    const match = anesthesiologists.find(a => {
      const lastName = (fullNameToLastNameRest(a.name) || '').toLowerCase();
      const fullName = (a.name || '').toLowerCase();
      return lastName === label || fullName === label;
    });

    return match?.id;
  }, [anesthesiologistId, anesthesiologists, anesthesiologistName]);

  const [setAnesthesiologist] = useMutation(editProcedureAnesthesiologist, {
    onError: onError ? error => onError(error, 'save') : undefined,
  });

  const handleSelect = async (anesthesiologistId: number | null) => {
    if (!date) return;

    const result = await setAnesthesiologist({
      variables: { id: procedureId, anesthesiologistId },
    });
    if (result.data?.editProcedureAnesthesiologist === null) {
      onError?.(new ApolloError({ errorMessage: 'The procedure is no longer available.' }), 'save');
    }
  };

  return (
    <PopupSelectMenu
      options={options}
      selectedId={selectedId}
      editable={editableAnesthesiologist}
      onSelect={handleSelect}
      onRemove={editableAnesthesiologist ? () => handleSelect(null) : undefined}
      removeOption={
        <Chip
          icon={<RemoveCircleOutline className={classes.iconRemove} />}
          label={'Remove Slot'}
          size="small"
          className={classes.chip}
        />
      }
      renderTrigger={({ onClick }) => (
        <ButtonBase
          className={classes.editLabel}
          onClick={onClick}
          aria-disabled={editableAnesthesiologist === false}
          tabIndex={editableAnesthesiologist === false ? -1 : 0}
          aria-label={
            editableAnesthesiologist === false
              ? anesthesiologistName || 'Anesthesiologist'
              : anesthesiologistName
                ? `Change anesthesiologist: ${anesthesiologistName}`
                : 'Assign anesthesiologist'
          }
          title={anesthesiologistName}
        >
          {anesthesiologistName ? (
            (anesthesiologistName || '-').substring(0, condensView ? 15 : undefined)
          ) : editableAnesthesiologist ? (
            <Icon>
              <AnesthesiaIcon width={22} height={22} />
            </Icon>
          ) : (
            '-'
          )}
        </ButtonBase>
      )}
    />
  );
};

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      maxHeight: '38rem',
      overflow: 'hidden',
    },
    menu: {
      maxHeight: '28rem',
      overflowY: 'scroll',
    },
    textFieldRoot: {
      margin: '1rem',
      marginBottom: 0,
      padding: 0,
    },
    inputRoot: {
      width: '14rem',
      fontSize: '0.8125em',
      padding: '0.25em',
    },
    searchIcon: {
      marginLeft: '0.25em',
      marginRight: '0.25em',
    },
    inputAdornedStart: {
      paddingLeft: 0,
    },
    spinnerContainer: {
      padding: '1rem',
    },
    editLabel: {
      font: 'inherit',
      maxWidth: '100%',
      display: 'block',
      textAlign: 'left',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    chip: {
      backgroundColor: 'transparent',
      cursor: 'pointer',
      color: theme.palette.text.primary,
    },
    iconRemove: {
      color: theme.palette.error.main,
    },
  })
);

export default memo(AnesthesiologistName);
